import React, { useEffect } from "react";
import { AddressFields, EmergencyContactFields, FullNameFields } from "~/pages/family/FieldGroups";
import { getUserAccountStatus, setUserAccountStatusOverride } from "~/shared/auth-utils";
import { useHistory } from 'react-router-dom';
import FloatingPanel from "~/shared/FloatingPanel";
import ControlledForm from "~/shared/form/ControlledForm";
import FieldContext from "~/shared/form/FieldContext";
import FormButton from "~/shared/form/FormButton";
import FormResponse from "~/shared/form/FormResponse";
import FormSection from "~/shared/form/FormSection";
import TextInput from "~/shared/form/TextInput";
import Page from "~/shared/Page";
import CheckboxInput from "~/shared/form/CheckboxInput";
import FormIconButton from "~/shared/form/FormIconButton";
import { useForm, useFieldArray } from "react-hook-form";
import { Form } from "react-bootstrap";

export default function StaffRegistration() {
    const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    const history = useHistory();
    const formHook = useForm();
    const { watch, formState: { errors } } = formHook;
    const fieldArrays = [];
    daysOfWeek.forEach((_, i) => fieldArrays.push(useFieldArray({ control: formHook.control, name: `availability.${i}.times` })));

    useEffect(() => {
        fieldArrays.forEach(fieldArray => fieldArray.append({}));
        return () => fieldArrays.forEach(fieldArray => fieldArray.remove(0));
    }, []);

    function isValidTimeRange(start, end) {
        if (!start || !end) {
            return true;
        }

        return new Date(`1/1/1 ${start}`) < new Date(`1/1/1 ${end}`);
    }

    function onSubmit(data) {
        data.availability = data.availability.filter(d => d.available).map(({ times }, i) => ({ day: daysOfWeek[i], times }));

        if (data.availability.length === 0) {
            formHook.setError("root.formResponse", { type: "error", message: "You must select at least one day you are available." });
            return false;
        }
    }

    function onSuccess() {
        setUserAccountStatusOverride("Onboarding Pending");
        history.push("/");
    }

    if (["Active", "Onboarding Pending"].includes(getUserAccountStatus())) {
        history.push("/");
        return null;
    }

    return (
        <Page title="Staff Registration">
            <div className="page-container">
                <FloatingPanel red title="Account Information">
                    <ControlledForm hook={formHook} onSubmit={onSubmit} post="staff/register" onSuccess={onSuccess}>
                        <p className="mb-4">
                            Fill out your information below. Required fields are marked with a star (*).
                        </p>
                        <div className="flex flex-col gap-3">
                            <FieldContext id="details">
                                <FormSection row wrap gap={1}>
                                    <FullNameFields />
                                </FormSection>
                                <FormSection row wrap gap={1}>
                                    <AddressFields />
                                </FormSection>
                                <FormSection row wrap gap={1} title="Emergency Contact" heading>
                                    <EmergencyContactFields />
                                </FormSection>
                            </FieldContext>
                            <FormSection title="Availability" heading>
                                <p className="mb-4">Check days you are available and enter the time range(s) you are available for that day.</p>
                                <div className="d-flex flex-column align-items-center gap-2">
                                    {daysOfWeek.map((day, i) => (
                                        <div key={day} className="border rounded shadow-sm p-3 w-fit">
                                            <FieldContext id={`availability.${i}`}>
                                                <div className="d-flex gap-3 align-items-start flex-wrap">
                                                    <div className="d-flex gap-2 align-items-center mt-2">
                                                        <CheckboxInput id="available" noMargin deps={fieldArrays[i].fields.map((_, j) => [`availability.${i}.times.${j}.start`, `availability.${i}.times.${j}.end`]).flat()} />
                                                        <span className="fw-bold fs-5 text-start" style={{ width: "8rem" }}>{day}</span>
                                                    </div>
                                                    <div className="d-flex flex-column gap-2">
                                                        {fieldArrays[i].fields.map((field, j) => (
                                                            <FieldContext id={`times.${j}`} key={field.id} disabled={!watch(`availability.${i}.available`)}>
                                                                <div className="d-flex flex-column">
                                                                    <div className="d-flex gap-2 align-items-center flex-wrap flex-sm-nowrap">
                                                                        <TextInput label="Start Time" id="start" validate={(start, { availability }) => isValidTimeRange(start, availability[i].times[j].end)} deps={`availability.${i}.times.${j}.end`} type="time" minWidth={10} maxWidth={10} hideLabel hideValidationMessage noMargin required />
                                                                        <span>-</span>
                                                                        <TextInput label="End Time" id="end" validate={(end, { availability }) => isValidTimeRange(availability[i].times[j].start, end)} deps={`availability.${i}.times.${j}.start`} type="time" minWidth={10} maxWidth={10} hideLabel hideValidationMessage noMargin required />
                                                                        {j === 0 ? (
                                                                            <FormIconButton legacy icon="bi-plus-lg" onClick={() => fieldArrays[i].append({})} />
                                                                        ) : (
                                                                            <FormIconButton legacy icon="bi-x-lg" onClick={() => fieldArrays[i].remove(j)} />
                                                                        )}
                                                                    </div>
                                                                    {errors.availability?.[i]?.times?.[j]?.start?.type === "validate" && (
                                                                        <Form.Control.Feedback type="invalid" className="d-block" style={{ marginTop: "-2px" }}>End time must be after start time.</Form.Control.Feedback>
                                                                    )}
                                                                </div>
                                                            </FieldContext>
                                                        ))}
                                                    </div>
                                                </div>
                                            </FieldContext>
                                        </div>
                                    ))}
                                </div>
                            </FormSection>
                        </div>
                        <br />
                        <FormResponse />
                        <FormButton submit className="mx-auto" />
                    </ControlledForm>
                </FloatingPanel>
            </div>
        </Page>
    );
}
